import { READ_ONLY_TOOLTIP } from '@/lib/accessRole';
import type { FillControls } from '@/lib/fillPermissions';
import type { PostHeadingCounts } from '@/lib/fillPost';
import { namedOf } from '@/lib/statusPresentation';
/**
 * The words the fill CONTROLS say.
 *
 * Page-level copy — the header, the footer, the dialogs — lives in
 * `pages/DutyFill/copy.ts`. Same split as `components/intake/copy.ts` versus
 * `pages/ArrangementBuilder/copy.ts`.
 */
/**
 * ⚠️ [C3c §3d] THE TWO WINDOW REFUSALS, AND NEITHER IS `READ_ONLY_TOOLTIP`.
 *
 * That string says "Your access is view-only", which is FALSE for a nodal whose access is fine
 * and whose duty has simply run — and it sends them to a super admin about a permission they
 * already hold. [C1f §7b]'s distinction: a blocker is a fact about the RECORD, a capability
 * refusal is a fact about the PERSON, and they are different sentences.
 */
export const FILL_WINDOW_COPY = {
  /** Naming has closed; swapping has not. The second sentence is the part that is actionable. */
  namingClosed: 'Naming closed an hour after this duty started. You can still swap a name until the shift ends.',
  /** Both have closed. ⚠️ [C3c §3] TRUE AGAIN — [PRE-P6.2] had made this sentence false, because
      its narrowed window could close while the duty was still running. */
  /**
   * ⚠️ [amended C3c.1] THE INVERTED CASE, WHICH IS REACHABLE AND WAS SILENT.
   *
   * Naming closes at `starts_at + 1h` and swapping at `ends_at`, and nothing makes a shift an
   * hour long — a 06:00–06:30 duty point closes swapping FIRST. Between 06:30 and 07:00 the `⋮`
   * offers Remove and not Swap, and before this string there was no sentence saying why.
   */
  swapClosed: 'This duty has ended, so a name can no longer be swapped. You can still add or remove one.',
  allClosed: 'This duty is over, so its names can no longer be changed.',
} as const;

/**
 * ⚠️ [C3c.2] THE CAPABILITY REFUSAL FOR A HOLDER OF `fulfilment.completeShortfall` AND NOTHING
 * ELSE — i.e. a `NODAL_MONITOR`.
 *
 * It is NOT in `FILL_WINDOW_COPY`: that object's own header is that a window refusal is a fact
 * about the RECORD, and this is a fact about the PERSON. It sits beside `READ_ONLY_TOOLTIP`
 * instead, as the narrower of the two — and it exists because that string ("Your access is
 * view-only.") became FALSE on this one screen the moment a monitor got a write button in its
 * footer. A tooltip that contradicts a live control two inches away is worse than no tooltip.
 */
export const SHORTFALL_ONLY_TOOLTIP = 'Your access is view-only apart from recording a short-fall.';

export const FILL_COPY = {
  /**
   * ⚠️ [C3c §4b] THE COUNT'S OWN SENTENCE. A bare integer beside a name says nothing to a screen
   * reader, and "90 days" is the one part of it an operator cannot infer from the number.
   */
  dutyCountLabel: (count: number): string => `${String(count)} duties in the last 90 days`,
  dutyCountLoading: 'Loading duty counts',
  /**
   * ⚠️ [C5 §11a] THE VISIBLE WORDS BESIDE THE NUMBER. A bare integer in a row of names says
   * nothing about what it counts, and the accessible sentence above was the only place that did
   * — which a sighted operator never hears.
   *
   * ⚠️ IT SAYS `PAs`, AND THE ACCESSIBLE SENTENCE STILL SAYS `duties`. They are not the same
   * thing and neither is wrong: the number counts ASSIGNMENTS (one per duty), and §11a's own
   * wording is what an operator calls them. The screen-reader sentence keeps the precise noun
   * and the window; the chip keeps the short one.
   */
  recentPas: (count: number): string => `${String(count)} Recent PAs`,
  /** ⚠️ [C5 §11b] The popover's heading, and its three rows are months. */
  recentPasTitle: 'Recent PAs',
  recentPasLoading: 'Loading recent PAs',
  recentPasFailed: 'Could not load recent PAs',
  /**
   * ⚠️ THE ACCESSIBLE NAME SAYS WHERE THE SLOT IS, because the chip itself shows only a `+`.
   * It is also the test contract: the twelve-name budget finds the next slot with
   * `getAllByRole('button', { name: /^Add / })[0]`, so two identically-named buttons would
   * break it with "found multiple elements" — an error that sends people to the wrong file.
   */
  addSlot: (line: string, index: number, total: number): string =>
    `Add ${line}, slot ${String(index + 1)} of ${String(total)}`,
  /**
   * ⚠️ [PRE-P6 §2d] THE RANK IS IN THE ACCESSIBLE NAME because it left the visible chip.
   * The chip shows a name; the one control on it still says who unambiguously.
   */
  chipActions: (officer: string, rank: string | null, line: string, index: number): string =>
    `More actions for ${officer}${rank ? ` (${rank})` : ''}, ${line} slot ${String(index + 1)}`,
  swap: 'Swap',
  remove: 'Remove',
  removeStaged: 'Remove',

  /* [C4a §2] The sheet matches a name, a GL number or a phone — the result is still a name. */
  searchLabel: 'Search by name, GL number or phone',
  searchPlaceholder: 'Type three letters',
  resultsLabel: 'Matching officers',
  /** Shown before anything is typed — the reason the common case is Enter alone. */
  recentHeading: 'Recently deployed here',
  matchesHeading: 'Matches',
  noneEligible: 'Nobody eligible is posted here yet.',
  noneMatching: 'No match. Try fewer letters.',
  alreadyNamed: 'already on this duty',
  /**
   * ⚠️ THE SHEET STATES WHERE IT IS AT ALL TIMES, and this is the only channel it has.
   * Radix marks everything outside the dialog `aria-hidden`, so the chip row behind it is
   * unreadable to a screen reader while the sheet is open — an operator filling twelve slots
   * without looking away must never be unsure which one they are on.
   */
  position: (line: string, index: number, total: number, scope: string): string =>
    `${line} · slot ${String(index + 1)} of ${String(total)}${scope ? ` · ${scope}` : ''}`,
  eligible: (ranks: readonly string[]): string =>
    ranks.length === 0 ? 'Any rank' : `Eligible ranks: ${ranks.join(', ')}`,
  /**
   * ⚠️ [PRE-P6 §2c] THE ROW'S DONE SIGNAL, WHICH USED TO BE THE SHEET CLOSING ITSELF.
   *
   * `allNamed` sat here since [P4] with ZERO call sites — §9's "no dead tooling" caught by
   * nobody, because a string is not a dependency. It has one now, reworded from "every slot"
   * to THIS post, which is the whole of what changed: the cursor no longer crosses into
   * another requirement row, so running out is a fact about one row.
   */
  rowComplete: (line: string): string => `Every slot on ${line} is named.`,
  rowCompleteHint: 'Close this to pick another post.',
  /** ⚠️ [PRE-P6.2] The duty's assignment history — see `DutyHistoryDrawer`. */
  historyTitle: 'Change history',
  historyTrigger: 'Change history',
  historyLoading: 'Loading…',
  historyEmpty: 'Nothing has been named on this duty yet.',
  historyBy: (actor: string): string => `by ${actor}`,
  historyTruncated: (shown: number, total: number): string =>
    `Showing the ${String(shown)} most recent of ${String(total)} changes.`,
  close: 'Close',
  /*
   * ⚠️ [amended C5c §5a] THE ATTENDANCE WORDS LEFT THIS FILE. The chip's word is the roster's own
   * now — `Present` / `Absent` through `presenceWord` (`lib/statusPresentation.ts`), U6 — rather
   * than six sentences of its own ("Started duty, did not reach", "Marked absent" for a clock-derived
   * absence too). CLAUDE.md §8 [C3c §7]'s rule stands: a word travels with the wash.
   */
} as const;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1b §3] THE SPLIT MODAL'S WORDS.
 *
 *  ⚠️ EVERY REFUSAL COMES FROM THE SERVER, NOT FROM HERE. `planDelegation` names the number
 *  AND the night, and the modal renders that sentence verbatim — a second wording on the
 *  client would be a second rule, and the two would disagree about which night binds.
 *  `refused` is the fallback for a failure with no message at all.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const DELEGATE_COPY = {
  title: (source: string): string => `Split "${source}"`,
  loading: 'Loading…',
  ok: 'Split',
  countLabel: 'Number for',
  /*
   * ⚠️ [C1h §3e] `totals` IS DELETED, WITH ITS REASON AT ITS GRAVE.
   *
   * It read `Delegated 0 · Retained 4 · Total 4`. §3e: "it restates the header count and adds
   * a scope disclaimer" — and only the SCOPE half carried anything. That half is `reach` below
   * and it stays, because it is the answer to whether a delegation covers the whole duty point
   * or one night, and it does cover the whole duty point.
   *
   * ⚠️ THE INVARIANT IT ANNOUNCED IS UNTOUCHED. `retained + Σ delegated = pool` is enforced by
   * `delegationPlan.util.ts` and asserted by `delegation.test.ts` CLAIM 1 on every night of a
   * three-night fixture. A caption never held it.
   */
  /** ⚠️ THE NIGHT, NOT JUST THE NUMBER — "you can only split 2" is unactionable without it. */
  ceiling: (named: number, on: string | null, delegable: number, pool: number): string =>
    on
      ? `${String(named)} are already named on ${on}, so at most ${String(delegable)} of these ${String(pool)} can be split.`
      : `${String(named)} are already named, so at most ${String(delegable)} of these ${String(pool)} can be split.`,
  /** ⚠️ ONE REQUIREMENT LINE SERVES EVERY NIGHT OF ITS DUTY POINT. Said before the click. */
  reach: (duties: number): string =>
    duties === 1 ? 'This applies to this duty.' : `This applies to all ${String(duties)} duties of this duty point.`,
  floor: (named: number): string => `${String(named)} named · cannot go below ${String(named)}`,
  noUnits: 'There are no units beneath this source to split it across.',
  refused: 'That split was refused.',
  /**
   * ⚠️ [amended C1f §1a] `trigger` IS GONE — the text link nobody found is replaced by the
   * two-way control on the post row. The strings below are what replaced it.
   */
  addUnit: 'Add another unit',
  confirm: 'Confirm',
  cancel: 'Cancel',
  unitLabel: 'Unit',
  countFor: (unit: string): string => `Number for ${unit}`,
  chooseUnit: 'Choose a unit',
  /** The picker's own placeholder, so an unfilled row reads as unfinished rather than empty. */
  pickUnit: 'Unit…',
} as const;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1f §1] THE POST ROW — routing, and the shortfall summary behind it.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
/*
 * The per-ADDRESSEE-ROW count (`alloc-count`) is `POST_COPY.namedOf`. ⚠️ [amended C4a §6] It is NOT
 * the post heading's any more — see `postCount` — and it never mixes delegated with named, because
 * one allocation row is either handed down or it is not. ⚠️ [amended C5b review] It is
 * `statusPresentation.ts`'s `namedOf` rather than a private copy of the same template (D10's "one
 * spelling of a fill count", which a second definition only kept true by coincidence).
 */

export const POST_COPY = {
  /**
   * ⚠️ [amended C3c §5a] TWO VERBS STILL, AND BOTH NOW NAME THEIR OBJECT. "Assign by Name"
   * and "Delegate to PS / Unit" are what the operator is about
   * to DO; "Naming" / "Delegation" would read as a state the row is already in, which is the
   * one thing this control must not imply before it is pressed.
   */
  name: 'Assign by Name',
  delegate: 'Delegate to PS / Unit',
  modeLabel: (post: string): string => `Who fills ${post}`,
  /** ⚠️ Rendered where the per-allocation dot used to be. See `FillLines`' one-dot rule. */
  namedOf,
  /**
   * ─────────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C4a §6] THE POST HEADING STATES ITS COMPONENTS AND NO COMPOSITE — superseding [C3a §5c]'s
   *  `postSentence` (`2 of 4 delegated · 2 named`), which is DELETED with this comment at its grave.
   *
   *  That sentence's "named" was the full `namedCount`, so a name a station put on a handed-down
   *  row was in BOTH clauses, and no reading of it summed to the post. The numbers here come from
   *  `postHeadingCounts`, whose addressee parts are `postConservation`'s and DO sum:
   *
   *    addressee  `3 named · 1 delegated · 2 to go`   — `to go` is what drives the operator
   *    oversight  `3 named · 3 not yet named`         — delegation is not fulfilment ([UX1 §3b])
   *
   *  ⚠️ NO `X of Y` ANYWHERE IN EITHER. A denominator invites exactly the misreading this exists to
   *  remove — "4 of 6" told the commissionerate an untruth and "3 of 6" told the division nothing.
   *
   *  ⚠️ THE `delegated` PART IS OMITTED AT ZERO, and only that part: a division that has handed
   *  nothing down is not told "0 delegated" on every post. `to go` is never omitted, including at
   *  zero, because it is the number the operator acts on.
   *
   *  ⚠️ AND THE REMAINDER WORD FOLLOWS THE AUDIENCE EVEN WITH NOTHING DELEGATED — `2 to go` versus
   *  `2 not yet named`. Same number, same dot; the word names WHOSE remainder it is. That reverses
   *  [UX1 §3]'s "the two audiences render the same words" control, which is restated as "same dot,
   *  same numbers" in both suites that carried it.
   * ─────────────────────────────────────────────────────────────────────────────────────
   */
  postCount: (counts: PostHeadingCounts): string =>
    counts.audience === 'addressee'
      ? [
          `${String(counts.filled)} named`,
          counts.delegated > 0 ? `${String(counts.delegated)} delegated` : null,
          `${String(counts.remaining)} to go`,
        ]
          .filter((part): part is string => part !== null)
          .join(' · ')
      : `${String(counts.named)} named · ${String(counts.unnamed)} not yet named`,

  /**
   * ⚠️ [C3a §5a] THE BRACKETS ARE COPY, NEVER A JSX LITERAL.
   *
   * §5a puts the count beside the post heading — `Sub-Inspector / Sergeant (2 of 4 named)` —
   * and a `(` typed into the component is a string no test can address and no translation can
   * reach. It is one function so both hosts and the matrix cannot disagree about the shape.
   */
  bracketed: (text: string): string => `(${text})`,
  /**
   * ⚠️ [C1f §1h] THE GAP, NOT THIRTY AVATARS. A division scanning a post it has split four
   * ways is asking what is still missing, so the collapsed line leads with the shortfall.
   */
  shortfall: (named: number, total: number): string =>
    named >= total
      ? `All ${String(total)} named`
      : `${String(named)} of ${String(total)} named · ${String(total - named)} to go`,
  /**
   * ⚠️ [C2a §5a] A VEHICLE IS COUNTED, NEVER NAMED — so its words carry no "named" and no
   * "of". `2 vans` is the whole statement: there is no denominator, because there is no
   * second number a vehicle column can have.
   */
  /** ⚠️ [C2a §5c] Neutral wording and a neutral tone — this screen's colour means FILL STATE. */
  delegatedFrom: 'Delegated to this unit',
  editSplit: 'Change',
  removeSplit: 'Remove',
  editSplitFor: (post: string): string => `Change how ${post} is split`,
  removeSplitFor: (unit: string, post: string): string => `Remove ${unit} from the split of ${post}`,
  /** ⚠️ [UX1 §2]'s cost, stated where it bites: a fully delegated post keeps no source row. */

  /**
   * ⚠️ [P7A §3] WHAT A VEHICLE LINE REQUIRES, AND NOTHING ELSE. "asked for" read as half of a sentence
   * whose other half — "sent" — a vehicle never has; the client's rule is that a vehicle states what
   * it requires and is never shown short anywhere.
   */
  vehicleCount: (count: number): string => `${String(count)} required`,
  vehicleNothing: 'None required',
  /** ⚠️ [P7A §3] Where a post would list who is named — there is no vehicle mapping to show. */
  vehicleNoMapping: 'No vehicle mapping yet',

  expand: 'Show the slots',
  collapse: 'Hide the slots',

  /** ⚠️ [C5c §2h] After the dot, never beside the name — `fillGaps.spec.ts` measures the tone's left half. */
  sharedAcross: 'Covers every duty point',
} as const;

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [C5c §2h] A COLUMN THIS DUTY IS COVERED FOR, FROM ANOTHER DUTY POINT — READ-ONLY.
 *
 *  ⚠️ "Name them on DP1" IS A LINK ONLY WHEN THE CALLER HOLDS A SHARE THERE. Otherwise it is the
 *  plain statement "Named on DP1": a link to a sheet that would answer with no lines is a promise
 *  the next click breaks.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const SHARED_LINES_COPY = {
  heading: 'Shared from another duty point',
  sharedFrom: (dutyPoint: string, title: string): string => `shared from ${dutyPoint} · ${title}`,
  nameThem: (dutyPoint: string): string => `Name them on ${dutyPoint}`,
  namedOn: (dutyPoint: string): string => `Named on ${dutyPoint}`,
  nobodyYet: 'Nobody named yet.',
  notVisible: 'Named by the division asked for it there.',
  /** ⚠️ [P7A §3] A shared VEHICLE column names nobody anywhere — it states where it is required. */
  vehicleOn: (dutyPoint: string): string => `Required on ${dutyPoint}`,
} as const;

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [C6a §4] A COMMAND ROLE ASKED OF A DIVISION, ON THE FILL SCREEN.
 *
 *  ⚠️ NO SENTENCE HERE STATES A NUMBER OF POSITIONS. A seat is `Awaited` or holds a name; a family is
 *  a role and a division; the only figures are the hand-down editor's own inputs, which are typed,
 *  not stated (FR-5.2.6b — a command position is never a number beside the force).
 *
 *  ⚠️ EVERY CONTROL NAMES ITS ROLE AND ITS DIVISION, because an oversight sheet can carry the same
 *  role asked of two divisions — and the position, for the seats of one family, which are otherwise
 *  indistinguishable. `position N` identifies a seat; it counts nothing.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const COMMAND_FAMILY_COPY = {
  heading: 'Command asked of the division',
  awaited: 'Awaited',
  /**
   * ⚠️ [C6a review] AN EMPTY SEAT ONCE NO NAME CAN REACH THIS DUTY — never "Awaited", a promise nobody can
   * keep. The record page and the preview say `Not named by CD`; each chip already shows its unit.
   */
  notNamed: 'Not named',
  namePlaceholder: 'Name an officer',
  nameLabel: (role: string, where: string, position: number): string =>
    `Name ${role} for ${where}, position ${String(position)}`,
  removeLabel: (officer: string, role: string): string => `Remove ${officer} from ${role}`,
  removeTitle: (officer: string, role: string): string => `Remove ${officer} as ${role}?`,
  removeIntro: 'The position goes back to awaiting a name, on every duty of this duty point still open for naming.',
  removeConfirm: 'Remove',
  named: 'Officer named',
  cleared: 'Name cleared',
  handedDown: 'Handed on',
  /* The two-value switch — plain buttons, the builder's own "By name / Ask a division" shape. */
  modeLabel: (role: string, division: string): string => `Who fills ${role} for ${division}`,
  nameHere: 'Name here',
  handDown: 'Hand to a unit',
  /* ⚠️ The visible words FIRST (WCAG 2.5.3, label in name), then which family — one per role and division. */
  nameHereFor: (role: string, division: string): string => `Name here: ${role} for ${division}`,
  handDownFor: (role: string, division: string): string => `Hand to a unit: ${role} for ${division}`,
  /* The hand-down editor. */
  loading: 'Loading…',
  noUnits: 'There are no units beneath this division to hand it to.',
  pickUnit: 'Unit…',
  unitLabel: (role: string, row: number): string => `Unit for ${role}, row ${String(row)}`,
  countFor: (role: string, unit: string): string => `How many ${role} positions for ${unit}`,
  /**
   * ⚠️ NOT "Add another unit", which is the force editor's words — and never anything starting `Add `.
   * This block renders ABOVE the force grid, and `DutyFill.test.tsx` finds the first empty force slot
   * with `getAllByRole('button', { name: /^Add / })[0]`: a command control matching it would be first.
   */
  addUnit: 'Hand to another unit',
  confirm: 'Confirm',
  cancel: 'Cancel',
  refused: 'That hand-down was refused.',
  /**
   * ⚠️ SAID BEFORE THE CLICK, because a seat answers for the whole duty point: a name reaches every
   * duty of the card whose naming window is still open, and a duty whose window has shut keeps the
   * commanders it has — `restampAskedCommand`'s rule, stated where the operator acts on it.
   */
  reach: 'Applies to every duty of this duty point still open for naming.',
} as const;

/**
 * ⚠️ [C3c §3d] WHY A CONTROL IS REFUSED, IN ONE SENTENCE, OR `undefined` WHEN IT IS NOT.
 *
 * `undefined` rather than `''`: an AntD `Tooltip` with an undefined title mounts NO node at all,
 * which is what keeps an available control out of `census.spec.ts`'s overlay arithmetic — the
 * mechanism [D4.3] recorded when it moved three read-only hints into `EXEMPT`.
 */
export const refusalFor = (controls: FillControls, verb?: 'name' | 'swap'): string | undefined => {
  /*
   * ⚠️ [amended C3c.1] A TOOLTIP ON AN **AVAILABLE** CONTROL IS A LIE, AND `swapClosed` CREATED
   * THE FIRST ONE.
   *
   * Before that notice existed, every state for which this returned a sentence had `mayName`
   * false too — so the string always described a control that was actually disabled, and passing
   * the whole `controls` object was harmless. `swapClosed` is the first arm where naming is OPEN
   * while swapping is shut: the three naming controls all wrap themselves in
   * `<Tooltip title={refusalFor(controls)}>`, so on a 06:00–06:30 duty at 06:45 they rendered
   * ENABLED with a tooltip announcing they were refused.
   *
   * ⚠️ SO THE VERB IS A PARAMETER, WHICH IS §3c's OWN DESIGN APPLIED TO THE MESSAGE. Five verbs
   * exist so a refusal can name its own reason; a message that cannot say WHICH verb it is about
   * is the collapse that design refuses, one layer up.
   *
   * ⚠️ AND OMITTING IT IS THE **BANNER'S** READING — "what is closed on this screen" — which is a
   * different question from "why is this control unavailable" and is the one call that must keep
   * answering under `swapClosed`.
   */
  if (verb === 'name' && controls.mayName) return undefined;
  if (verb === 'swap' && controls.maySwap) return undefined;

  if (controls.notice === 'noCapability') return READ_ONLY_TOOLTIP;
  if (controls.notice === 'shortfallOnly') return SHORTFALL_ONLY_TOOLTIP;
  if (controls.notice === 'namingClosed') return FILL_WINDOW_COPY.namingClosed;
  if (controls.notice === 'swapClosed') return FILL_WINDOW_COPY.swapClosed;
  if (controls.notice === 'allClosed') return FILL_WINDOW_COPY.allClosed;
  return undefined;
};
